/**
 * @module components/Hero
 * @description Brief §4.1: rotating 4–5 image hero, headline, two scroll
 * buttons, keyword search, and the impact strip.
 *
 * Auto-rotation pauses on hover/focus, has a visible pause button (WCAG 2.2.2),
 * and does not auto-advance under prefers-reduced-motion.
 */
import { useEffect, useState, type FormEvent, type JSX } from "react";
import { HERO_COPY, HERO_INTERVAL_MS, HERO_SLIDES, impactFigures, type ImpactFigure } from "../config/page";
import { Icon } from "./Icons";
import "./Hero.css";

/** Props for <Hero>. */
interface HeroProps {
    /** Count of causes on the page, for the impact strip. */
    causeCount: number;
    /** Runs a keyword search in the browse section. */
    onSearch: (text: string) => void;
}

/** @returns true when the visitor asked for reduced motion. */
function prefersReducedMotion(): boolean {
    return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Scrolls smoothly (or instantly, under reduced motion) to a section id. */
export function scrollToSection(id: string): void {
    document.getElementById(id)?.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
}

/** The page hero. */
export function Hero({ causeCount, onSearch }: HeroProps): JSX.Element {
    const [index, setIndex] = useState<number>(0);
    const [paused, setPaused] = useState<boolean>(prefersReducedMotion);
    const [hovering, setHovering] = useState<boolean>(false);
    const [text, setText] = useState<string>("");

    useEffect(() => {
        if (paused || hovering) return;
        const id: number = window.setInterval(() => setIndex((i: number) => (i + 1) % HERO_SLIDES.length), HERO_INTERVAL_MS);
        return () => window.clearInterval(id);
    }, [paused, hovering]);

    const submit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        onSearch(text);
        scrollToSection("browse");
    };

    const figures: ImpactFigure[] = impactFigures(causeCount);

    return (
        <section className="hero" aria-roledescription="carousel" aria-label="Featured photos"
            onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
            <div className="hero__slides">
                {HERO_SLIDES.map((slide, i: number) => (
                    <img key={slide.src} src={slide.src} alt={i === index ? slide.alt : ""} aria-hidden={i !== index}
                        className={`hero__slide${i === index ? " is-active" : ""}`} loading={i === 0 ? "eager" : "lazy"} />
                ))}
                <div className="hero__scrim" />
            </div>

            <div className="container hero__content">
                <p className="kicker hero__kicker">{HERO_COPY.kicker}</p>
                <h1 className="hero__headline">{HERO_COPY.headline}</h1>
                <p className="hero__subline">{HERO_COPY.subline}</p>

                <form className="hero__search" role="search" onSubmit={submit}>
                    <label htmlFor="hero-search" className="visually-hidden">Search causes</label>
                    <Icon name="search" />
                    <input id="hero-search" type="search" value={text} placeholder={HERO_COPY.searchPlaceholder}
                        onChange={(e) => setText(e.target.value)} autoComplete="off" />
                    <button type="submit" className="btn btn--gold">Search</button>
                </form>

                <div className="hero__ctas">
                    <button type="button" className="btn btn--primary btn--lg hero__cta-primary" onClick={() => scrollToSection("browse")}>
                        {HERO_COPY.browseCta} <Icon name="arrow-right" />
                    </button>
                    <button type="button" className="btn btn--on-dark btn--lg" onClick={() => scrollToSection("featured")}>
                        {HERO_COPY.featuredCta}
                    </button>
                </div>
            </div>

            <div className="hero__controls container">
                <div className="hero__dots" role="tablist" aria-label="Choose photo">
                    {HERO_SLIDES.map((slide, i: number) => (
                        <button key={slide.src} type="button" role="tab" aria-selected={i === index} aria-label={`Photo ${i + 1} of ${HERO_SLIDES.length}`}
                            className={`hero__dot${i === index ? " is-active" : ""}`} onClick={() => setIndex(i)} />
                    ))}
                </div>
                <button type="button" className="hero__pause" onClick={() => setPaused((p: boolean) => !p)} aria-label={paused ? "Play slideshow" : "Pause slideshow"}>
                    <Icon name={paused ? "play" : "pause"} size={16} />
                </button>
            </div>

            <div className="impact-strip">
                <div className="container impact-strip__inner">
                    {figures.map((f: ImpactFigure) => (
                        <div key={f.label} className="impact-strip__item">
                            <span className="impact-strip__value">{f.value}</span>
                            <span className="impact-strip__label">{f.label}{f.source === "sample" && <span className="visually-hidden"> (sample data)</span>}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
